/**
 * RTWO Validator Infrastructure — Domain Models
 * Phase 9 | Module ID: RTWO-VALD-001
 * Version: 1.0.0-phase9
 *
 * Strongly typed domain models for the RTWO Validator Infrastructure.
 * Defines the enterprise validator management layer for validator lifecycle,
 * node binding, stake references, performance sources, penalties, and lifecycle records.
 *
 * Truthful Data Policy:
 * - No fake validators, voting power, stake, uptime, missed blocks, or penalties.
 * - Private keys are NEVER stored, displayed, or returned.
 * - Validator signing is handled exclusively through the Cryptography & Identity Engine.
 * - Validator status must NEVER be "Active" unless approved and connected to real validator infrastructure.
 * - All runtime values are null until real validator backend services are connected.
 *
 * Source: Validator Infrastructure (RTWO-VALD-001)
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type ValidatorId = string;
export type BindingId = string;
export type StakeId = string;
export type PerformanceId = string;
export type PenaltyId = string;
export type RecordId = string;
export type NodeId = string;
export type WalletId = string;
export type PublicKeyRef = string; // Reference only — never the actual key
export type StakeRef = string; // Reference only — never the actual stake amount
export type NodeRef = string; // Reference only — never the actual node config
export type ValidatorTimestamp = number | null;

export const VALIDATOR_INFRASTRUCTURE_VERSION = {
  moduleId: 'RTWO-VALD-001',
  moduleName: 'Validator Infrastructure',
  version: '1.0.0-phase9',
  phase: 'Phase 9 — Validator Infrastructure',
  status: 'Structure Complete — Waiting for Validator Nodes',
  productionReadiness: 'Pending Validator Nodes',
  policy:
    'Truthful Data Policy Enforced — No Fake Validators, Voting Power, Stake, Uptime, or Penalties',
} as const;

// ─────────────────────────────────────────────
// VALIDATOR STATUS (LIFECYCLE)
// ─────────────────────────────────────────────

/**
 * Validator Lifecycle:
 * Candidate → Pending Review → Approved → Active → Inactive → Jailed → Slashed → Removed
 *
 * SECURITY NOTE: "Active" status must NEVER be set unless the validator is
 * approved AND connected to real validator infrastructure.
 */
export type ValidatorStatus =
  | 'candidate'
  | 'pending_review'
  | 'approved'
  | 'active'
  | 'inactive'
  | 'jailed'
  | 'slashed'
  | 'removed';

export interface ValidatorLifecycleStage {
  status: ValidatorStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresRealInfrastructure: boolean;
  securityNote: string;
}

export const VALIDATOR_LIFECYCLE: ValidatorLifecycleStage[] = [
  {
    status: 'candidate',
    label: 'Candidate',
    description:
      'Validator has submitted a registration request. Identity and operator address assigned. Awaiting review.',
    terminal: false,
    requiresRealInfrastructure: false,
    securityNote: 'Candidate exists as a record only. No voting power or stake assigned yet.',
  },
  {
    status: 'pending_review',
    label: 'Pending Review',
    description: 'Validator registration is under review by the governance or operator committee.',
    terminal: false,
    requiresRealInfrastructure: false,
    securityNote: 'Review process must verify node binding and stake reference before approval.',
  },
  {
    status: 'approved',
    label: 'Approved',
    description:
      'Validator has been approved. Node binding and stake reference confirmed. Awaiting activation.',
    terminal: false,
    requiresRealInfrastructure: true,
    securityNote: 'Approval requires verified node binding and confirmed stake reference.',
  },
  {
    status: 'active',
    label: 'Active',
    description:
      'Validator is active and participating in consensus. Connected to real validator infrastructure.',
    terminal: false,
    requiresRealInfrastructure: true,
    securityNote:
      'CRITICAL: Active status requires real validator node connection. Must not be set without real infrastructure.',
  },
  {
    status: 'inactive',
    label: 'Inactive',
    description:
      'Validator has been deactivated. Not participating in consensus. Stake remains locked.',
    terminal: false,
    requiresRealInfrastructure: false,
    securityNote:
      'Inactive validators retain their stake reference but cannot participate in consensus.',
  },
  {
    status: 'jailed',
    label: 'Jailed',
    description:
      'Validator has been jailed due to misbehavior or missed blocks. Temporarily excluded from consensus.',
    terminal: false,
    requiresRealInfrastructure: false,
    securityNote:
      'Jailed validators cannot participate in consensus until unjailed via governance.',
  },
  {
    status: 'slashed',
    label: 'Slashed',
    description:
      'Validator has been slashed. A portion of stake has been penalized due to protocol violation.',
    terminal: false,
    requiresRealInfrastructure: false,
    securityNote:
      'Slashing is a severe penalty. Slashed validators may be removed after governance review.',
  },
  {
    status: 'removed',
    label: 'Removed',
    description:
      'Validator has been permanently removed from the validator set. Retained for audit purposes.',
    terminal: true,
    requiresRealInfrastructure: false,
    securityNote: 'Removed validators are read-only. All operations are permanently blocked.',
  },
];

export const VALIDATOR_STATUS_LABELS: Record<ValidatorStatus, string> = {
  candidate: 'Candidate',
  pending_review: 'Pending Review',
  approved: 'Approved',
  active: 'Active',
  inactive: 'Inactive',
  jailed: 'Jailed',
  slashed: 'Slashed',
  removed: 'Removed',
};

export const VALIDATOR_STATUS_COLORS: Record<ValidatorStatus, string> = {
  candidate: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  pending_review: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  approved: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  active: 'text-green-400 bg-green-400/10 border-green-400/20',
  inactive: 'text-slate-400 bg-slate-400/10 border-slate-400/20',
  jailed: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  slashed: 'text-red-400 bg-red-400/10 border-red-400/20',
  removed: 'text-slate-600 bg-slate-600/10 border-slate-600/20',
};

// ─────────────────────────────────────────────
// NODE TYPE
// ─────────────────────────────────────────────

export type ValidatorNodeType =
  'full_node' | 'archive_node' | 'sentry_node' | 'validator_node' | 'light_node';

// ─────────────────────────────────────────────
// STAKE STATUS
// ─────────────────────────────────────────────

export type StakeStatus =
  'pending' | 'locked' | 'active' | 'unbonding' | 'unbonded' | 'slashed' | 'released';

// ─────────────────────────────────────────────
// PENALTY TYPE & SEVERITY
// ─────────────────────────────────────────────

export type PenaltyType =
  | 'double_sign'
  | 'missed_blocks'
  | 'downtime'
  | 'invalid_vote'
  | 'protocol_violation'
  | 'governance_violation';

export type PenaltySeverity = 'warning' | 'minor' | 'major' | 'critical' | 'terminal';

export type PenaltyStatus = 'pending' | 'applied' | 'appealed' | 'reversed' | 'archived';

// ─────────────────────────────────────────────
// PERFORMANCE SOURCE STATUS
// ─────────────────────────────────────────────

export type PerformanceSourceStatus =
  'not_connected' | 'waiting_for_data' | 'structure_ready' | 'connected' | 'error';

// ─────────────────────────────────────────────
// BINDING STATUS
// ─────────────────────────────────────────────

export type BindingStatus = 'pending' | 'active' | 'inactive' | 'revoked';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOValidator
// ─────────────────────────────────────────────

/**
 * RTWOValidator — Core validator record.
 *
 * Security:
 * - publicKeyReference is a reference ID only — never the actual public key bytes.
 * - stakeReference is a reference ID only — never the actual stake amount.
 * - nodeReference is a reference ID only — never the actual node configuration.
 * - validatorStatus must NEVER be "active" without real validator infrastructure.
 * - votingPower is null until real consensus data is available.
 */
export interface RTWOValidator {
  validatorId: ValidatorId;
  operatorAddress: string | null; // null until real address assigned via Cryptography Engine
  publicKeyReference: PublicKeyRef | null; // Reference only — never the actual key
  validatorStatus: ValidatorStatus;
  votingPower: null; // Always null — Truthful Data Policy: no fake voting power
  stakeReference: StakeRef | null; // Reference only — never the actual stake amount
  nodeReference: NodeRef | null; // Reference only — never the actual node config
  joinedAt: ValidatorTimestamp;
  updatedAt: ValidatorTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOValidatorNodeBinding
// ─────────────────────────────────────────────

/**
 * RTWOValidatorNodeBinding — Binds a validator to a specific node.
 *
 * A validator must have an active node binding before it can be approved.
 * Node binding is verified through the P2P Network Engine.
 */
export interface RTWOValidatorNodeBinding {
  bindingId: BindingId;
  validatorId: ValidatorId;
  nodeId: NodeId | null; // null until real node is registered
  nodeType: ValidatorNodeType;
  status: BindingStatus;
  createdAt: ValidatorTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOValidatorStake
// ─────────────────────────────────────────────

/**
 * RTWOValidatorStake — Stake reference record for a validator.
 *
 * Truthful Data Policy:
 * - amount is null — no fake stake amounts.
 * - lockedAmount is null — no fake locked amounts.
 * - All amounts come from the Ledger & State Engine when connected.
 */
export interface RTWOValidatorStake {
  stakeId: StakeId;
  validatorId: ValidatorId;
  ownerWalletId: WalletId | null; // null until real wallet is connected
  amount: null; // Always null — Truthful Data Policy: no fake stake
  lockedAmount: null; // Always null — Truthful Data Policy: no fake locked amounts
  stakeStatus: StakeStatus;
  createdAt: ValidatorTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOValidatorPerformance
// ─────────────────────────────────────────────

/**
 * RTWOValidatorPerformance — Performance data source references for a validator.
 *
 * Truthful Data Policy:
 * - uptimeSource, missedBlocksSource, latencySource are source references only.
 * - No fake uptime percentages, missed block counts, or latency values.
 * - All performance data must come from real validator node connections.
 */
export interface RTWOValidatorPerformance {
  performanceId: PerformanceId;
  validatorId: ValidatorId;
  uptimeSource: 'not_connected'; // Always "not_connected" — Truthful Data Policy
  missedBlocksSource: 'not_connected'; // Always "not_connected" — Truthful Data Policy
  latencySource: 'not_connected'; // Always "not_connected" — Truthful Data Policy
  status: PerformanceSourceStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOValidatorPenalty
// ─────────────────────────────────────────────

/**
 * RTWOValidatorPenalty — Penalty record for a validator.
 *
 * Truthful Data Policy:
 * - No fake penalties are created.
 * - Penalty records only exist when real validator infrastructure reports a violation.
 */
export interface RTWOValidatorPenalty {
  penaltyId: PenaltyId;
  validatorId: ValidatorId;
  penaltyType: PenaltyType;
  severity: PenaltySeverity;
  reason: string;
  status: PenaltyStatus;
  createdAt: ValidatorTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOValidatorLifecycleRecord
// ─────────────────────────────────────────────

/**
 * RTWOValidatorLifecycleRecord — Immutable audit record of validator status transitions.
 *
 * Every status change must produce a lifecycle record for audit and compliance.
 */
export interface RTWOValidatorLifecycleRecord {
  recordId: RecordId;
  validatorId: ValidatorId;
  previousStatus: ValidatorStatus | null; // null for initial creation
  newStatus: ValidatorStatus;
  reason: string;
  timestamp: ValidatorTimestamp;
}

// ─────────────────────────────────────────────
// VALIDATOR ENGINE STATUS
// ─────────────────────────────────────────────

export interface ValidatorEngineStatus {
  moduleId: 'RTWO-VALD-001';
  moduleName: 'Validator Infrastructure';
  phase: 'Phase 9';
  status: 'Structure Complete';
  productionReadiness: 'Pending Validator Nodes';
  validatorCount: null; // Always null — Truthful Data Policy
  activeValidators: null; // Always null — Truthful Data Policy
  totalVotingPower: null; // Always null — Truthful Data Policy
  totalStake: null; // Always null — Truthful Data Policy
  networkConnection: 'Not Connected to Validator Nodes';
  mainnetStatus: 'Not Connected to Mainnet';
  dataSource: 'Validator Infrastructure — Structure Complete — Waiting for Real Validator Data';
}

export const VALIDATOR_ENGINE_INITIAL_STATUS: ValidatorEngineStatus = {
  moduleId: 'RTWO-VALD-001',
  moduleName: 'Validator Infrastructure',
  phase: 'Phase 9',
  status: 'Structure Complete',
  productionReadiness: 'Pending Validator Nodes',
  validatorCount: null,
  activeValidators: null,
  totalVotingPower: null,
  totalStake: null,
  networkConnection: 'Not Connected to Validator Nodes',
  mainnetStatus: 'Not Connected to Mainnet',
  dataSource: 'Validator Infrastructure — Structure Complete — Waiting for Real Validator Data',
};

// ─────────────────────────────────────────────
// VALIDATOR SERVICE IDS
// ─────────────────────────────────────────────

export const VALIDATOR_SERVICE_IDS = {
  VALIDATOR_REGISTRY: 'validator-registry-service',
  VALIDATOR_ONBOARDING: 'validator-onboarding-service',
  VALIDATOR_LIFECYCLE: 'validator-lifecycle-service',
  NODE_BINDING: 'validator-node-binding-service',
  STAKE_REFERENCE: 'validator-stake-reference-service',
  PERFORMANCE_SOURCE: 'validator-performance-source-service',
  PENALTY: 'validator-penalty-service',
  ELIGIBILITY: 'validator-eligibility-service',
  EVENT_PUBLISHER: 'validator-event-publisher',
  READINESS: 'validator-readiness-service',
} as const;

// ─────────────────────────────────────────────
// VALIDATOR EVENTS
// ─────────────────────────────────────────────

export type ValidatorEventType =
  | 'validator_registered'
  | 'validator_approved'
  | 'validator_activated'
  | 'validator_deactivated'
  | 'validator_jailed'
  | 'validator_slashed'
  | 'validator_removed'
  | 'node_binding_created'
  | 'stake_reference_created'
  | 'penalty_applied'
  | 'lifecycle_record_created';

export interface ValidatorEvent {
  eventId: string;
  eventType: ValidatorEventType;
  validatorId: ValidatorId;
  timestamp: number;
  payload: Record<string, unknown>;
}
